import React, { useState } from 'react';
import { Package, Printer, ChevronRight, Check } from 'lucide-react';
import { paperCoreProducts, printSupplies } from '../data/mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const ProductsSection = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  return (
    <section id="products" className="py-24 bg-neutral-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-widest">Our Products</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Premium Paper Cores & Print Supplies
          </h2>
          <p className="mt-6 text-lg text-neutral-400 max-w-2xl mx-auto">
            Quality cardboard cores, paper tubes, and print supplies manufactured in Perth for Australian industries.
          </p>
        </div>

        {/* Product Tabs */}
        <Tabs defaultValue="cores" className="w-full">
          <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 bg-neutral-800/50 p-1 rounded-xl mb-12">
            <TabsTrigger 
              value="cores" 
              className="data-[state=active]:bg-amber-400 data-[state=active]:text-neutral-900 rounded-lg py-3 font-semibold transition-all"
            >
              <Package className="w-4 h-4 mr-2" />
              Paper Cores
            </TabsTrigger>
            <TabsTrigger 
              value="supplies"
              className="data-[state=active]:bg-amber-400 data-[state=active]:text-neutral-900 rounded-lg py-3 font-semibold transition-all"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Supplies
            </TabsTrigger>
          </TabsList>

          {/* Paper Cores Tab */}
          <TabsContent value="cores" className="mt-0">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paperCoreProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-neutral-950 border border-neutral-800 rounded-2xl p-6 hover:border-amber-400/50 transition-all duration-500 cursor-pointer"
                  onClick={() => setSelectedProduct(selectedProduct === product.id ? null : product.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-amber-400/10 rounded-xl flex items-center justify-center group-hover:bg-amber-400/20 transition-colors">
                      <Package className="w-7 h-7 text-amber-400" />
                    </div>
                    <ChevronRight className={`w-5 h-5 text-neutral-500 transition-transform duration-300 ${
                      selectedProduct === product.id ? 'rotate-90' : 'group-hover:translate-x-1'
                    }`} />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-amber-400 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-neutral-400 text-sm mb-4">{product.description}</p>
                  
                  {/* Available Sizes */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.sizes.slice(0, 4).map((size, idx) => (
                      <span key={idx} className="px-2 py-1 bg-neutral-800 text-neutral-300 text-xs rounded-md">
                        {size}
                      </span>
                    ))}
                    {product.sizes.length > 4 && (
                      <span className="px-2 py-1 bg-neutral-800 text-amber-400 text-xs rounded-md">
                        +{product.sizes.length - 4} more
                      </span>
                    )}
                  </div>

                  {/* Expanded Applications */}
                  <div className={`overflow-hidden transition-all duration-500 ${
                    selectedProduct === product.id ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="pt-4 border-t border-neutral-800">
                      <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Applications</p>
                      <ul className="space-y-2">
                        {product.applications.map((app, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-neutral-300">
                            <Check className="w-4 h-4 text-amber-400" />
                            {app}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Print Supplies Tab */}
          <TabsContent value="supplies" className="mt-0">
            <div className="grid md:grid-cols-2 gap-6">
              {printSupplies.map((supply) => (
                <div
                  key={supply.id}
                  className="group bg-neutral-950 border border-neutral-800 rounded-2xl p-8 hover:border-amber-400/50 transition-all duration-500 overflow-hidden"
                >
                  {supply.image && (
                    <div className="-mx-8 -mt-8 mb-6 h-48 border-b border-neutral-800 overflow-hidden">
                      <img
                        src={supply.image}
                        alt={supply.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center group-hover:bg-amber-400/20 transition-colors">
                      <Printer className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white group-hover:text-amber-400 transition-colors">
                      {supply.name}
                    </h3>
                  </div>
                  <p className="text-neutral-400">{supply.description}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-neutral-400 mb-6">Need a custom size or specification? We can help.</p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-neutral-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors"
          >
            Request Custom Quote
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
